import React, {FC} from 'react';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, RendererOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useDeleteDraftOfflinePermission} from '../../../../../../srcCommon/permissions/useOfflinePermission';
import {PermissionResult} from '../../../../../helpers/permissionHelpers';
import {DraftMessageModel} from '../../../../../models/api/messageModel';
import {useIsMessageVisuallyShared} from '../../../../../store/data/messages/messagesSelectors';
import {PermissionToolbarButton} from '../../../../common/buttons/permissionToolbarButton';
import {OkCancelDialog} from '../../../../common/dialogs/okCancelDialog';

/*
 * Props.
 */

export interface DeleteToolbarButtonProps {
  message: DraftMessageModel;
  deleteMessage: Handler;
}

interface DraftOfflinePermissionCheckProps {
  message: DraftMessageModel;
  children: RendererOf<PermissionResult>;
}

/*
 * Component.
 */

export const DeleteToolbarButton: FC<DeleteToolbarButtonProps> = (props) => {
  // Check if the provided message was already saved.
  const {message, deleteMessage} = props;
  const messageWasSaved = message.updatedAt > 0;

  // If not, we won't need a confirmation.
  if (!messageWasSaved) {
    return renderButton(props, deleteMessage);
  }

  // Otherwise, show a confirm dialog on click.
  return (
    <PopoverCoordinator
      renderButton={() => renderButton(props)}
      renderModal={(modalProps) => (
        <DeleteConfirmationDialog modalProps={modalProps} message={message} onConfirmClick={deleteMessage} />
      )}
    />
  );
};

/*
 * Button.
 */

function renderButton(props: DeleteToolbarButtonProps, deleteMessage?: Handler) {
  const {message} = props;
  return (
    <UserThemeConsumer>
      {(theme) => (
        <DraftOfflinePermissionCheck message={message}>
          {(permission) => (
            <PermissionToolbarButton
              iconName="trash"
              onClick={deleteMessage}
              label={<Trans id="composer-delete">Delete</Trans>}
              offlinePermission={permission}
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
            />
          )}
        </DraftOfflinePermissionCheck>
      )}
    </UserThemeConsumer>
  );
}

/*
 * Dialog.
 */

interface DeleteConfirmationDialogProps {
  modalProps: PopoverTransferProps;
  message: DraftMessageModel;
  onConfirmClick: Handler;
}

const DeleteConfirmationDialog: FC<DeleteConfirmationDialogProps> = (props) => {
  const {modalProps, message, onConfirmClick} = props;
  const isMessageVisuallyShared = useIsMessageVisuallyShared(message);

  // Showing a warning for regular drafts.
  if (!isMessageVisuallyShared) {
    return (
      <OkCancelDialog
        {...modalProps}
        title={<Trans id="composer-delete-title">Delete draft</Trans>}
        okButtonLabel={<Trans id="composer-delete-ok">Delete</Trans>}
        onOkClick={onConfirmClick}
      >
        <Trans id="composer-delete-content">
          Are you sure you want to delete this draft?
          <br />
          This action cannot be undone.
        </Trans>
      </OkCancelDialog>
    );
  }

  // Shared drafts.
  // Wording is different for a draft only conversations.
  if (!message.inReplyToId) {
    return (
      <OkCancelDialog
        {...modalProps}
        title={<Trans id="composer-delete-shareddraft-new-title">Delete conversation</Trans>}
        okButtonLabel={<Trans id="composer-delete-shareddraft-new-ok">Delete</Trans>}
        onOkClick={onConfirmClick}
      >
        <Trans id="composer-delete-shareddraft-new-content">
          Deleting this shared draft will delete the entire conversation for all participants. This cannot be
          undone.
        </Trans>
      </OkCancelDialog>
    );
  }

  // Regular shared draft delete warning.
  return (
    <OkCancelDialog
      {...modalProps}
      title={<Trans id="composer-delete-shareddraft-title">Delete draft</Trans>}
      okButtonLabel={<Trans id="composer-delete-shareddraft-ok">Delete</Trans>}
      onOkClick={onConfirmClick}
    >
      <Trans id="composer-delete-shareddraft-content">
        Deleting this shared draft will delete it for all participants. This cannot be undone.
      </Trans>
    </OkCancelDialog>
  );
};

const DraftOfflinePermissionCheck: FC<DraftOfflinePermissionCheckProps> = (props) => {
  const offlinePermission = useDeleteDraftOfflinePermission([props.message]);

  return <>{props.children(offlinePermission)}</>;
};
